import React from "react";
import styled from "styled-components";
import { WorkoutStepPayload } from "./types";
import { TargetSummary } from "./Target";

const StepContainer = styled.div`
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.03);
  padding: 1.35rem 1.5rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35);
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const StepBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: rgba(59, 130, 246, 0.18);
  color: #1d4ed8;
`;

const IntensityPill = styled.span<{ $bg: string; $color: string }>`
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$color};
`;

const Details = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  color: #475569;
`;

const DetailRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.9rem;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #1f2937;
`;

const NestedSteps = styled.div`
  margin-top: 1.15rem;
  padding-left: 1.1rem;
  display: grid;
  gap: 0.9rem;
  border-left: 2px solid rgba(99, 102, 241, 0.35);
`;

const MetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.08);
  font-size: 0.8rem;
  font-weight: 500;
  color: #1f2937;
`;

interface WorkoutStepProps {
  step: WorkoutStepPayload;
}

const intensityStyles: Record<string, { bg: string; color: string }> = {
  WARMUP: { bg: "rgba(16, 185, 129, 0.15)", color: "#0f766e" },
  COOLDOWN: { bg: "rgba(125, 211, 252, 0.18)", color: "#0369a1" },
  ACTIVE: { bg: "rgba(59, 130, 246, 0.18)", color: "#1d4ed8" },
  INTERVAL: { bg: "rgba(147, 51, 234, 0.18)", color: "#6d28d9" },
  RECOVERY: { bg: "rgba(234, 179, 8, 0.18)", color: "#b45309" },
  REST: { bg: "rgba(148, 163, 184, 0.25)", color: "#475569" },
};

const WorkoutStep: React.FC<WorkoutStepProps> = ({ step }) => {
  const intensityStyle =
    intensityStyles[step.intensity] || intensityStyles.ACTIVE || {
      bg: "rgba(148, 163, 184, 0.2)",
      color: "#334155",
    };

  const durationDisplay = formatDuration(step.durationType, step.durationValue);
  const repeatSummary =
    step.type === "WorkoutRepeatStep" ? formatRepeat(step.repeatType, step.repeatValue, step.steps) : null;

  return (
    <StepContainer>
      <StepHeader>
        <StepBadge>Step {step.stepOrder}</StepBadge>
        <IntensityPill $bg={intensityStyle.bg} $color={intensityStyle.color}>
          {step.intensity || "—"}
        </IntensityPill>
      </StepHeader>

      <Details>
        {step.description && (
          <DetailRow>
            <DetailLabel>Description</DetailLabel>
            <span>{step.description}</span>
          </DetailRow>
        )}
        <DetailRow>
          <DetailLabel>Duration</DetailLabel>
          <MetaChip>{durationDisplay}</MetaChip>
        </DetailRow>
        {repeatSummary && (
          <DetailRow>
            <DetailLabel>Repeat</DetailLabel>
            <MetaChip>{repeatSummary}</MetaChip>
          </DetailRow>
        )}
        <TargetSummary
          targetType={step.targetType}
          targetValue={step.targetValue}
          targetValueLow={step.targetValueLow}
          targetValueHigh={step.targetValueHigh}
        />
      </Details>

      {step.type === "WorkoutRepeatStep" && step.steps && step.steps.length > 0 && (
        <NestedSteps>
          {step.steps.map((nestedStep) => (
            <WorkoutStep key={`${step.stepOrder}-${nestedStep.stepOrder}`} step={nestedStep} />
          ))}
        </NestedSteps>
      )}
    </StepContainer>
  );
};

function formatDuration(type: string, value: number) {
  const normalized = type?.toUpperCase?.() ?? "";

  switch (normalized) {
    case "TIME":
    case "FIXED_REST": {
      const minutes = Math.floor(value / 60);
      const seconds = Math.round(value % 60);
      if (minutes === 0) return `${seconds}s`;
      if (seconds === 0) return `${minutes} min`;
      return `${minutes}m ${seconds}s`;
    }
    case "DISTANCE": {
      if (value >= 1000) {
        const kilometres = value / 1000;
        const rounded = Number.isInteger(kilometres) ? kilometres : kilometres.toFixed(2);
        return `${rounded} km`;
      }
      return `${value} m`;
    }
    case "REPS":
      return `${value} reps`;
    case "OPEN":
      return "Open";
    case "CALORIES":
      return `${value} kcal`;
    default:
      return `${value} ${normalized.replace(/_/g, " ").toLowerCase()}`;
  }
}

function formatRepeat(
  repeatType?: string,
  repeatValue?: number | null,
  steps?: WorkoutStepPayload[]
) {
  if (!repeatType && !repeatValue) {
    return null;
  }

  const readableType = repeatType
    ? repeatType
        .replace("REPEAT_UNTIL_", "")
        .replace(/_/g, " ")
        .toLowerCase()
    : "repeat";

  if (repeatValue && repeatValue > 0) {
    return `${repeatValue}× • ${readableType}`;
  }

  if (steps && steps.length > 0) {
    return `${steps.length} steps • ${readableType}`;
  }

  return readableType;
}

export default WorkoutStep;
